import { useQuery, useQueryClient } from '@tanstack/react-query';
import { devAPI } from 'api/api';
import React, { useEffect, useState } from 'react';
import pollingService, { PoolingRequestData } from 'tools/pollingService';
import { ImageDataType } from 'components/element-image/index';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { SetupImageForm } from 'components/element-image/editor/form/SetupImageForm';
import useBundleTranslation from 'i18n';
import { YNValues } from 'tools/types';

interface CollectImageRequestType {
    sv_id: number;
    width: number;
    height: number;
    force_recollect?: YNValues;
}

export default function SetupImageFormWrapper({
    settingsUrl,
    onHide,
    src,
    setupImgEnabled,
    segmentValueId,
    origImgSizeEnabled,
    pluginName,
}: {
    settingsUrl: string;
    onHide: (recollectImage: boolean) => void;
    src: string;
    setupImgEnabled: boolean;
    segmentValueId: number;
    origImgSizeEnabled: boolean;
    pluginName: string;
}) {
    const { t } = useBundleTranslation();
    const queryClient = useQueryClient();

    const { data } = useQuery<any, Error>({
        queryKey: [settingsUrl],
        queryFn: () => devAPI.getEditorImage(settingsUrl),
    });

    // Display Loading Mask
    const [isCollectionRunning, setIsCollectionRunning] = useState<boolean>(true);
    const [imageBase64, setImageBase64] = useState('');

    const collectImage = (width: number, height: number, force: boolean) => {
        setIsCollectionRunning(true);
        const onSuccess = function (data: any) {
            setIsCollectionRunning(false);
            // Update stamp to refresh Image
            setImageBase64(data.image);
        };
        const onError = function (data: any) {
            setIsCollectionRunning(false);
            setImageBase64('');
            alert(data.message);
        };

        const requestData: CollectImageRequestType & PoolingRequestData = {
            poolingCreateURL: src,
            sv_id: segmentValueId,
            width: width,
            height: height,
        };
        if (force) {
            requestData.force_recollect = 'Y';
        }

        const { create } = pollingService<any, CollectImageRequestType>({ onSuccess, onError, requestData });
        create();
    };

    const [imageData, setImageData] = useState<ImageDataType>();

    const handleOnFormHide = (afterSubmit: boolean, recollectImage: boolean) => {
        onHide(recollectImage);
        setTimeout(() => {
            if (afterSubmit) {
                queryClient.removeQueries({ queryKey: [settingsUrl] });
            }
        });
    };

    useEffect(() => {
        if (typeof data == 'undefined') {
            return;
        }
        setImageData(data.data.data);
    }, [data]);

    useEffect(() => {
        if (!imageData) {
            return;
        }
        collectImage(imageData.width, imageData.height, false);
    }, [src, imageData]);

    if (typeof imageData == 'undefined') {
        return <LoadingPlaceholder />;
    }

    return (
        <SetupImageForm
            isCollectionRunning={isCollectionRunning}
            settingsUrl={settingsUrl}
            onHide={handleOnFormHide}
            imageData={imageData}
            imageBase64={imageBase64}
            setupImgEnabled={setupImgEnabled}
            recollectImage={collectImage}
            origImgSizeEnabled={origImgSizeEnabled}
            pluginName={pluginName}
        />
    );
}
